import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { useValidatePromoCodeEcomm, useRedeemPromoCodeEcomm } from "@ncs/ncs-api"
import { TextInput, ButtonEcomm, Paragraph, Chip } from "@ncs/web-legos/components"
import { couponStyle, appliedCoupons } from "./styles"
import { VoucherifyDiscount } from "../../CheckoutEcomm"
import { unpythonify } from "@ncs/ts-utils"

interface VoucherInputProps {
	cartTotal: number
	onDiscountsApplied: (discount: VoucherifyDiscount | null) => void
	setIsVoucherLoading: (value: boolean) => void
}

export interface VoucherInputHandle {
	redeemVouchers: () => Promise<boolean>
}

export const VoucherInput = forwardRef<VoucherInputHandle, VoucherInputProps>(
	({ cartTotal, onDiscountsApplied, setIsVoucherLoading }, ref) => {
		const [voucherCode, setVoucherCode] = useState<string>("")
		const [voucherifyErrorMessage, setVoucherifyErrorMessage] = useState("")
		const [appliedVouchers, setAppliedVouchers] = useState<string[]>([])
		const [isValidating, setIsValidating] = useState(false)

		const validatePromoCode = useValidatePromoCodeEcomm()
		const redeemPromoCode = useRedeemPromoCodeEcomm()

		// Expose redeemVouchers method to parent component via ref
		useImperativeHandle(
			ref,
			() => ({
				redeemVouchers: async () => {
					if (appliedVouchers.length === 0) return true

					try {
						const response = await redeemPromoCode({
							promoCodes: appliedVouchers,
							orderAmount: cartTotal * 100,
						})

						const redemptionResponse = unpythonify(response.data)
						return redemptionResponse?.success ?? false
					} catch (error) {
						console.error("Failed to redeem promo codes:", error)
						return false
					}
				},
			}),
			[appliedVouchers, cartTotal, redeemPromoCode]
		)

		// Validate and redeem vouchers using the ecomm service
		const validateAll = async (vouchers: string[] = appliedVouchers) => {
			setIsVoucherLoading(true)
			setIsValidating(true)
			setVoucherifyErrorMessage("")

			try {
				// 1. Validate the promo codes first
				const vResponse = await validatePromoCode({
					promoCodes: vouchers,
					orderAmount: cartTotal * 100,
				})

				const validationResponse = unpythonify(vResponse.data)

				if (!validationResponse?.data?.valid) {
					setVoucherifyErrorMessage("Invalid Code")
					onDiscountsApplied(null)
					return
				}

				const discount: VoucherifyDiscount = {
					totalAmount: validationResponse.data.order.totalAmount,
					totalDiscountAmount: validationResponse.data.order.totalDiscountAmount,
				}
				setAppliedVouchers(vouchers)
				onDiscountsApplied(discount)
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : "Voucher not valid."
				setVoucherifyErrorMessage(errorMessage)
				onDiscountsApplied(null)
			} finally {
				setIsVoucherLoading(false)
				setIsValidating(false)
			}
		}

		// Apply desire voucher/s
		const handleApply = () => {
			if (appliedVouchers.includes(voucherCode)) {
				setVoucherifyErrorMessage("This voucher was already applied")
				return
			}

			const newList = [...appliedVouchers, voucherCode]
			void validateAll(newList)
			setVoucherCode("")
		}

		// Unwanted vouchers are deleted
		const deleteVoucher = (code: string) => {
			const newList = appliedVouchers.filter((c) => c !== code)
			setAppliedVouchers(newList)
			setVoucherifyErrorMessage("")

			if (newList.length > 0) {
				void validateAll(newList)
			} else {
				onDiscountsApplied(null)
			}
		}

		/*
 	The voucher validation hook wont be called if:
  		1. The shopping cart is empty.
		2. No vouchers are applied.
  */
		useEffect(() => {
			if (appliedVouchers.length === 0) return

			if (cartTotal === 0) {
				setAppliedVouchers([])
				onDiscountsApplied(null)
				return
			}

			void validateAll()
		}, [cartTotal])

		return (
			<>
				<div css={couponStyle}>
					<TextInput
						value={voucherCode}
						type="text"
						icon="ticket-simple"
						placeholder="Enter Promo Code"
						onChange={(value) => setVoucherCode(value ?? "")}
					/>
					<ButtonEcomm
						buttonText={isValidating ? "VALIDATING" : "APPLY"}
						variant="secondary-cta"
						onClick={handleApply}
						disabled={isValidating || !voucherCode}
					/>
				</div>

				{voucherifyErrorMessage && (
					<Paragraph customColor="red" small mb={1}>
						{voucherifyErrorMessage}
					</Paragraph>
				)}

				{appliedVouchers.length > 0 && (
					<div css={appliedCoupons}>
						{appliedVouchers.map((code) => (
							<div key={code} className="coupon-chip">
								<Chip
									label={code}
									variant="square"
									onDelete={() => deleteVoucher(code)}
								/>
							</div>
						))}
					</div>
				)}
			</>
		)
	}
)
